/**
 * Sezzle Widget Manager
 * Handles linking custom Sezzle widgets with the original hidden widget
 * and manages price updates across different contexts (product, cart, drawer)
 */

class SezzleWidgetManager {
  constructor() {
    this.originalWidget = null;
    this.customWidgets = [];
    this.initialized = false;

    this.init();
  }

  init() {
    document.addEventListener('DOMContentLoaded', () => {
      this.setupOriginalWidget();
      this.setupCustomWidgets();
      this.setupEventListeners();
      this.initialized = true;
    });
  }

  setupOriginalWidget() {
    this.originalWidget = document.getElementById('sezzle-original-widget');
    if (!this.originalWidget) {
      console.warn('Sezzle: Original widget container not found. Make sure the hidden widget section is added to the footer.');
      return;
    }

    // Wait for Sezzle to initialize and render the widget
    this.waitForSezzleWidget();
  }

  waitForSezzleWidget(attempts = 0) {
    const maxAttempts = 50; // 5 seconds max wait

    if (attempts >= maxAttempts) {
      console.warn('Sezzle: Original widget not found after maximum attempts');
      // Try to link custom widgets anyway
      this.linkCustomWidgets();
      return;
    }

    // Check for the Sezzle button in the hidden container
    const sezzleButton = document.querySelector('#sezzle-hidden-widget-container .sezzle-shopify-info-button');

    if (sezzleButton) {
      console.log('Sezzle: Original widget found and ready');
      this.linkCustomWidgets();
    } else {
      setTimeout(() => {
        this.waitForSezzleWidget(attempts + 1);
      }, 100);
    }
  }

  setupCustomWidgets() {
    // No longer needed - using event delegation instead
    console.log('Sezzle: Using event delegation for custom widgets');
  }

  setupEventListeners() {
    // Only listen for manual price updates if needed
    document.addEventListener('sezzle:price-update', (event) => {
      this.handlePriceUpdate(event.detail);
    });
  }

  linkCustomWidgets() {
    // Use event delegation since widgets are constantly added/removed
    document.addEventListener('click', this.handleCustomWidgetClickDelegate.bind(this));
    document.addEventListener('keydown', this.handleCustomWidgetKeyDelegate.bind(this));

    console.log('Sezzle: Event delegation set up for custom widgets');
  }

  handleCustomWidgetClickDelegate(event) {
    // Check if clicked element is within a custom Sezzle widget
    const widget = event.target.closest('.sezzle-widget-custom');
    if (widget) {
      event.preventDefault();
      this.handleCustomWidgetClick(widget);
    }
  }

  handleCustomWidgetKeyDelegate(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      const widget = event.target.closest('.sezzle-widget-custom');
      if (widget) {
        event.preventDefault();
        this.handleCustomWidgetClick(widget);
      }
    }
  }

  handleCustomWidgetClick(widget) {
    const price = widget.getAttribute('data-price');
    const context = widget.getAttribute('data-context');
    const variantId = widget.getAttribute('data-variant-id');

    console.log(`Sezzle: Custom widget clicked - Price: ${price}, Context: ${context}, Variant: ${variantId}`);

    // Update the original widget with current price
    this.updateOriginalWidget(price, variantId);

    // Trigger the original widget modal
    this.triggerOriginalModal();
  }

  updateOriginalWidget(price, variantId = '') {
    if (!this.originalWidget) return;

    // Update data attributes for modal functionality
    this.originalWidget.setAttribute('data-price', price);

    if (variantId) {
      this.originalWidget.setAttribute('data-variant-id', variantId);
    }
  }

  triggerOriginalModal() {
    // Try the original Sezzle widget button first
    const originalSezzleButton = document.querySelector('#sezzle-hidden-widget-container .sezzle-shopify-info-button');
    if (originalSezzleButton) {
      originalSezzleButton.click();
      console.log('Sezzle: Original Sezzle button triggered');
      return;
    }

    // Fallback: try any Sezzle button on the page
    const anySezzleButton = document.querySelector('.sezzle-shopify-info-button');
    if (anySezzleButton) {
      anySezzleButton.click();
      console.log('Sezzle: Any available Sezzle button triggered');
      return;
    }

    // Last resort: try modal links
    const anyModalLink = document.querySelector('.sezzle-modal-link');
    if (anyModalLink) {
      anyModalLink.click();
      console.log('Sezzle: Modal link triggered as fallback');
      return;
    }

    console.warn('Sezzle: No Sezzle widget found to trigger modal');
  }

  // Removed handleCartUpdate and handleVariantChange since Liquid handles updates automatically

  handlePriceUpdate(data) {
    console.log('Sezzle: Manual price update event', data);

    const { price, variantId } = data;

    // Only update the original widget for modal functionality
    this.updateOriginalWidget(price, variantId);
  }

  // Removed widget price update methods since Liquid handles all price updates automatically

  formatMoney(amount) {
    // Basic money formatting - this should match Shopify's money format
    // You might want to use Shopify.formatMoney if available
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(amount);
    }

    // Fallback formatting
    return '$' + (amount / 100).toFixed(2);
  }

  // Public method to trigger modal with specific price
  triggerModalWithPrice(price, variantId = null) {
    this.updateOriginalWidget(price, variantId);
    this.triggerOriginalModal();
  }

  // Public method to trigger price update event (for manual updates only)
  triggerPriceUpdate(price, variantId = null) {
    const event = new CustomEvent('sezzle:price-update', {
      detail: { price, variantId }
    });
    document.dispatchEvent(event);
  }

  // Public method to refresh widget manager
  refresh() {
    // Event delegation is already set up, just log refresh
    console.log('Sezzle: Widget manager refreshed');
  }
}

// Initialize the Sezzle Widget Manager
window.sezzleWidgetManager = new SezzleWidgetManager();

// Expose utility functions globally
window.SezzleUtils = {
  // Trigger modal with specific price
  triggerModal: (price, variantId) => {
    window.sezzleWidgetManager.triggerModalWithPrice(price, variantId);
  },

  // Manual price update (only for original widget)
  updatePrice: (price, variantId) => {
    window.sezzleWidgetManager.triggerPriceUpdate(price, variantId);
  },

  // Refresh widget manager
  refresh: () => {
    window.sezzleWidgetManager.refresh();
  }
};
